export {
  VLM_SERVICE_TIERS,
  normalizeVlmServiceTier,
  vlmServiceTierLabel,
} from '../utils/providerCatalog.js';

export const SUPPORTED_IMAGE_ACCEPT =
  '.png,.jpg,.jpeg,.webp,.heic,.heif,image/png,image/jpeg,image/webp,image/heic,image/heif';
export const SUPPORTED_VIDEO_ACCEPT =
  '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime';

export function el(tag, className = '', attrs = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (key === 'text') node.textContent = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key === 'dataset') Object.assign(node.dataset, value);
    else if (key === 'style' && typeof value === 'string')
      node.setAttribute('style', value);
    else if (key in node) node[key] = value;
    else node.setAttribute(key, value);
  });
  return node;
}

export function button(className, text, onClick, attrs = {}) {
  const node = el('button', className, { type: 'button', ...attrs });
  if (text) node.textContent = text;
  if (onClick) node.addEventListener('click', onClick);
  return node;
}

export function maskIcon(src, className = 'w-4 h-4') {
  const node = el('span', `${className} inline-block shrink-0`);
  node.style.backgroundColor = 'currentColor';
  node.style.mask = `url("${src}") center / contain no-repeat`;
  node.style.webkitMask = `url("${src}") center / contain no-repeat`;
  return node;
}

export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function statusText(status) {
  const value = status === 'queued' ? 'running' : status;
  return (
    {
      pending: 'Pending',
      running: 'Running',
      completed: 'Done',
      failed: 'Failed',
      partial: 'Partial',
      canceled: 'Canceled',
      preview: 'Preview',
      reference: 'Reference',
    }[value] || 'Running'
  );
}

export function isActiveStatus(status) {
  return ['pending', 'queued', 'running'].includes(status);
}

export function formatDuration(start, end) {
  if (!start) return '0:00';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const seconds = Math.max(0, Math.floor((endMs - startMs) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function activeSequence(state) {
  return (
    state.sequences.find(
      sequence => String(sequence.id) === String(state.selectedSequenceId),
    ) || null
  );
}

export function activeStep(state) {
  const sequence = activeSequence(state);
  return (
    sequence?.steps?.find(
      step => String(step.id) === String(state.selectedStepId),
    ) || null
  );
}

export function activeJob(state) {
  return (
    state.jobs.find(job => String(job.id) === String(state.activeJobId)) ||
    state.jobs[0] ||
    null
  );
}

// An attached file shown on the canvas before any run, so regions, marks, and
// calibration points can be drawn on it. Coordinates are fractions of the image,
// so a shape drawn here applies to every photo the condition later runs on.
export function previewItem(state) {
  const files = state.previewFiles || [];
  const index = state.previewIndex;
  const file = index === null || index === undefined ? null : files[index];
  if (!file) return null;
  return {
    id: `preview:${index}:${file.name}:${file.size}`,
    name: file.name,
    status: 'preview',
    file,
    detections: [],
    resultMetadata: {},
  };
}

// A reference image put on the canvas from the condition editor, so markers can be
// drawn on the example itself. It carries no detections and no result metadata.
export function canvasImage(state) {
  const subject = state.canvasSubject;
  if (subject?.kind === 'reference' && subject.url) {
    return {
      id: `reference:${subject.imageId}`,
      name: subject.name || 'Reference',
      status: 'reference',
      url: subject.url,
      detections: [],
      resultMetadata: {},
    };
  }
  return previewItem(state) || activeJobItem(state);
}

export function activeJobItem(state) {
  const job = activeJob(state);
  return (
    job?.imageResults?.find(
      item => String(item.id) === String(state.selectedJobItemId),
    ) ||
    job?.imageResults?.[0] ||
    null
  );
}

// The step a result belongs to, from the live sequence definitions; a preview has
// no result, so it shows the step selected in the sidebar.
export function stepForItem(state, item) {
  const stepId = item?.stepId;
  if (!stepId) return activeStep(state);
  for (const sequence of state?.sequences || []) {
    const step = (sequence.steps || []).find(
      candidate => String(candidate.id) === String(stepId),
    );
    if (step) return step;
  }
  return activeStep(state);
}

// The coordinate space of a result's detections. The worker reports the size it ran
// on; a preview or a reference has none, and then nothing can be clipped.
export function itemImageSize(item) {
  const metadata = item?.resultMetadata || item?.metadata || {};
  const width = Number(
    metadata.imageWidth ?? metadata.image_width ?? item?.imageWidth,
  );
  const height = Number(
    metadata.imageHeight ?? metadata.image_height ?? item?.imageHeight,
  );
  return width > 0 && height > 0 ? { width, height } : null;
}
